import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Iter 77 — Mobile sidebar drawer state.
 *
 * The authenticated DAM uses a 280px fixed sidebar on desktop. On
 * screens < lg, that sidebar collapses into an off-canvas drawer that
 * slides in from the left. This context is the single source of truth
 * for whether the drawer is open; TopBar.jsx renders the hamburger
 * that toggles it, Sidebar.jsx reads it to drive its transform.
 *
 * The drawer auto-closes on every route change so a user who taps a
 * folder in the drawer lands on the folder page with the drawer out
 * of the way.
 */
const MobileSidebarContext = createContext({ open: false, setOpen: () => {}, toggle: () => {} });

export function MobileSidebarProvider({ children }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((p) => !p), []);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  // Lock body scroll while the drawer is open so touch dragging the
  // drawer doesn't scroll the page underneath it.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  return (
    <MobileSidebarContext.Provider value={{ open, setOpen, toggle }}>
      {children}
    </MobileSidebarContext.Provider>
  );
}

export function useMobileSidebar() {
  return useContext(MobileSidebarContext);
}
